import SearchIcon from "@mui/icons-material/Search";
import { Container } from "@mui/material";
import ModalBox from "components/Modal";
import { updatePaymentPageData } from "components/Payment/utils";
import { Spinner } from "components/shared/Spinner";
import { removeAccents } from "helpers/utils";
import { reloadAfterCompanySwitch } from "hooks/companySwitchLocation";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { changeAdminCompany, getCompanyList } from "store/adminPanelSlice";
import { fetchCompanyInfo } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import StyledTextField from "../../shared/StyledTextField/StyledTextField";
import { sortCompaniesWithExactIdFirst } from "./companyListSearch";
import SwitchCompanyModal from "./SwitchCompanyModal";

const CompaniesList = () => {
    const [t] = useTranslation();
    const { data: companies, isLoading } = useAppSelector((state) => state.adminPanel.companies);
    // const companySettings = useSelector((state: RootState) => state.settings.companySettings);
    const [filterValue, setFilterValue] = useState("");
    const [showSwitchCompany, setShowSwitchCompany] = useState(0);

    const handleClick = (companyId: number, reason: string) => {
        dispatch(changeAdminCompany({ companyId, reason })).then(() => {
            localStorage.setItem("companyId", companyId.toString());
            sessionStorage.setItem("payment_page_data", "{}");
            reloadAfterCompanySwitch();
        });
    };

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(getCompanyList());
        dispatch(fetchCompanyInfo());
    }, [dispatch]);

    const filteredCompanies = useMemo(() => {
        const notSorted = companies.filter((company) => {
            const _filterValue = removeAccents(filterValue).toLowerCase();
            // Handle filter company by name or ids
            const nameMatches = removeAccents(company.name.toLowerCase()).includes(_filterValue);
            const idMatches = String(company.id).includes(filterValue);
            return nameMatches || idMatches;
        });
        return sortCompaniesWithExactIdFirst(notSorted, filterValue);
    }, [filterValue, companies]);

    const handleChangeFilterValue = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setFilterValue(e.target.value);

    return (
        <div className="admin-list-container">
            <ModalBox
                onClose={() => setShowSwitchCompany(0)}
                mainTitle={t("AdminPanel.SwitchCompanyModal")}
                open={showSwitchCompany !== 0}
            >
                <SwitchCompanyModal
                    companyId={showSwitchCompany}
                    close={() => setShowSwitchCompany(0)}
                    handleSwitchCompany={handleClick}
                />
            </ModalBox>

            <div className="admin-search">
                <StyledTextField
                    placeholder="Search"
                    value={removeAccents(filterValue)}
                    onChange={handleChangeFilterValue}
                    sx={{ width: "550px" }}
                    InputProps={{
                        endAdornment: <SearchIcon sx={{ color: "#A8A8A8" }} />,
                    }}
                />
            </div>
            {isLoading ? (
                <div>
                    <Spinner />
                </div>
            ) : (
                filteredCompanies.map((company, index) => {
                    const previousCompany = index === 0 ? company : filteredCompanies[index - 1];
                    let letter = company.name.charAt(0).toLowerCase();
                    letter = letter == "é" ? "e" : letter;
                    const prevLetter =
                        previousCompany.name.charAt(0) == "É"
                            ? previousCompany.name.charAt(0).toLowerCase().replace("é", "e")
                            : previousCompany.name.charAt(0).toLowerCase();
                    const isNewLetter = index == 0 || !(letter == prevLetter);

                    return (
                        <>
                            {isNewLetter && (
                                <>
                                    <div className="bpstatement-newperiod-separator">
                                        <span>{letter.toUpperCase()}</span>
                                    </div>
                                </>
                            )}
                            <Container
                                key={company.id}
                                sx={{
                                    backgroundColor: "#FDFDFD",
                                    borderRadius: ".5rem",
                                    border: "solid 1px #EEEEEE",
                                }}
                                maxWidth={false}
                            >
                                <div key={`${company.id}-name`}>
                                    <h6>{t("AdminPanel.NameCompany")}</h6>
                                    <div className="company-name-highlight">
                                        {company.name} #{company.id}
                                    </div>
                                </div>
                                <div key={`${company.id}-address`}>
                                    <h6>{t("AdminPanel.Address")}</h6>
                                    <span>{company.address}</span>
                                </div>
                                <div key={`${company.id}-date`}>
                                    <h6>{t("AdminPanel.CompanyCreatedAt")}</h6>
                                    <span>{company.created.toLocaleString().split("T")[0]}</span>
                                </div>
                                <div key={`${company.id}-period`}>
                                    <h6>{t("AdminPanel.PeriodDuration")}</h6>
                                    <span>{company.periodDuration ?? "-"}</span>
                                </div>
                                <div key={`${company.id}-siret`}>
                                    <h6>{t("AdminPanel.Siret")}</h6>
                                    <span>{company.siret}</span>
                                </div>
                                <div key={`${company.id}-type`}>
                                    <h6>{t("AdminPanel.PublisherType")}</h6>
                                    <span>{company.publisherType}</span>
                                </div>

                                <div className="d-flex">
                                    <button
                                        className="btn-switch"
                                        onClick={() =>
                                            company.name.includes("Crealo") ||
                                            !window.location.href.includes("manage.crealo.app")
                                                ? handleClick(company.id, "")
                                                : setShowSwitchCompany(company.id)
                                        }
                                    >
                                        Switch to this company
                                    </button>
                                </div>
                            </Container>
                        </>
                    );
                })
            )}
        </div>
    );
};

export default CompaniesList;
